/**
 * HeroImageCover — full-bleed background image with a dark gradient
 * scrim and the title overlaid bottom-left. When no hero image is
 * available on the report, falls through to a flat brand-color
 * background that preserves the same layout.
 *
 * Used by energy-major seed templates. Designed to read more like
 * a board-deck cover than a technical report cover.
 */
import React from 'react';
import { PreparedForLine } from '../../PreparedForLine';
import {
  resolveHeroCoverBackground,
  resolveHeroCoverScrim,
  resolveHeroCoverTextTone,
  resolveHeroCoverIsLayoutDiagram,
  resolveHeroSvgGradientStops,
  isLikelySvgUrl,
} from '../../../../lib/resolveHeroCoverBackground';
import { DEFAULT_PRIMARY_COVER_LOGO_URL } from '../../../../lib/reportBranding';
import { roleColor, roleColorAlpha } from '../../../../lib/reportRoleColors';
import { reportTypographySizes } from '../../../../lib/reportSectionHeading';
import type { CoverProps } from './types';
import { CoverCustomerLogo, CoverPrimaryLogo } from './CoverCustomerLogo';
import { HeroCoverBoundaryOverlay } from './HeroCoverBoundaryOverlay';
import { resolveAbstractExcerpt, resolveAbstractFull, truncateCoverStandfirst, resolveCoverTitle } from '../../../../lib/reportRendering/journalCoverData';
import { RecoloredHeroSvg } from './RecoloredHeroSvg';
import { ReportPlatformCredit } from '../ReportPlatformCredit';
import { REPORT_PAGE_MARGIN_MM } from '../../../../lib/reportLayoutTokens';
import {
  COVER_HERO_FULL_PAGE_MIN_H_CLASS,
  COVER_HERO_COMPACT_MIN_H_CLASS,
} from '../../../../lib/reportCoverTokens';

function pickHeroImageUrl(report: CoverProps['report']): string | undefined {
  // Prefer an explicit hero on the report; fall through to the
  // theme logo or any included image-document the site has marked.
  return (report as { heroImageUrl?: string }).heroImageUrl
    || (report.documents ?? []).find(d => d.includeInReport && d.mimeType?.startsWith('image/'))?.rawStoragePath
    || undefined;
}

export const HeroImageCover: React.FC<CoverProps> = ({
  report, theme, branding, isAdmin,
  pickerOpen, onTogglePicker, onSelectArchetype,
  disclaimer,
  heroCover,
  boundedStandfirst = false,
}) => {
  // L-id-leak — never fall back to the raw Firestore `report.id`.
  const fallbackSiteWordmark = report.siteName?.trim() || 'Unnamed site';
  // EYEBROW SITE NAME (P2) — the cover eyebrow reads `branding.siteWordmark`,
  // which `resolveReportBranding` derives from the SITE ENTITY and degrades to
  // the 'Unnamed site' sentinel when the site entity is absent (e.g. the report
  // is fed without a resolved site, the QA harness `site:null` case). The
  // report ALWAYS carries its own `report.siteName` ("Holly Ridge"), so prefer
  // it whenever the branding wordmark fell to the sentinel — the eyebrow must
  // never read 'UNNAMED SITE' when the report knows its name.
  const resolveEyebrowWordmark = (brandingWordmark: string): string => {
    const wm = brandingWordmark.trim();
    if (wm && wm !== 'Unnamed site') return wm;
    return fallbackSiteWordmark;
  };
  const effectiveBranding = branding ?? {
    audienceMode: 'internal',
    siteWordmark: fallbackSiteWordmark,
    primaryLogoUrl: theme.logoUrl ?? DEFAULT_PRIMARY_COVER_LOGO_URL,
    secondaryLogoUrl: null,
    primaryLogoAlt: theme.companyName ?? 'Primary logo',
    secondaryLogoAlt: 'Customer logo',
  };
  // P3 — title from the ONE shared resolver (config-label source of truth).
  // Sourcing from getReportType() keeps the cover in sync with future relabels
  // and fixes the prior gaps (financial / public-sentiment fell to the BTM
  // default; executive-summary used a stale 'Executive Summary' string).
  const titleLabel = resolveCoverTitle(report);
  const rawExecutiveSummary = (report as { executiveSummary?: string }).executiveSummary;
  const heroExecutiveSummary = boundedStandfirst
    ? truncateCoverStandfirst(rawExecutiveSummary) || resolveAbstractExcerpt(report)
    : rawExecutiveSummary?.replace(/\s+/g, ' ').trim() || resolveAbstractFull(report) || null;
  const heroUrl = pickHeroImageUrl(report);
  // COVER PREVIEW FIX (TC1): the resolved report hero image was computed
  // here but `reportImageUrl` was hardcoded to `undefined`, so a report
  // whose hero comes from the report itself (report.heroImageUrl or an
  // included image-document) — NOT from a template `heroCover.imageUrl`
  // upload — silently fell back to the flat gradient and the photo only
  // showed as a small side thumbnail. Feed the resolved url through so
  // `resolveHeroCoverBackground` (and the matching text-tone/scrim logic)
  // can use it as the full-bleed background. Template-uploaded
  // `heroCover.imageUrl` still wins (resolved first inside the util).
  const bgInput = { settings: heroCover, theme, reportImageUrl: heroUrl };
  const background = resolveHeroCoverBackground(bgInput);
  const usesImage = background.startsWith('url(');
  const textTone = resolveHeroCoverTextTone(bgInput);
  const onDark = textTone === 'on-dark';
  // When the photo is already the full-bleed background, do not ALSO render
  // it in the side panel (avoids a duplicated image). The side panel is then
  // reserved for the boundary / KMZ overlay only.
  const sideImageUrl = usesImage ? undefined : heroUrl;
  // HERO LAYOUT (TC2): `full-page` (default) fills the cover frame;
  // `compact` renders a shorter banner. Absent → full-page (back-compat).
  const heroLayout = theme.heroLayout ?? 'full-page';
  const isCompact = heroLayout === 'compact';
  const hasBoundaryOverlay = (report.siteBoundaries?.length ?? 0) > 0 || (report.siteBoundary?.length ?? 0) > 0;
  const hasKmzLayerOverlay = (effectiveBranding.kmzLayers?.length ?? 0) > 0;
  // HERO-BG (#93) — recolour controls + generated-diagram-as-background.
  const heroSvgStrokeColor = heroCover?.heroSvgStrokeColor?.trim() || undefined;
  const heroSvgGradient = resolveHeroSvgGradientStops(heroCover);
  // `layout-diagram` mode paints the generated boundary diagram full-bleed.
  // Falls back to the gradient base alone when the report has no geometry.
  const isLayoutDiagramMode = resolveHeroCoverIsLayoutDiagram(heroCover)
    && (hasBoundaryOverlay || hasKmzLayerOverlay);
  // Optional composite: diagram over the chosen background (NOT as the sole
  // background). Skipped when the diagram is already the full-bleed background.
  const compositeLayoutDiagram = !isLayoutDiagramMode
    && heroCover?.heroIncludeLayoutDiagram === true
    && (hasBoundaryOverlay || hasKmzLayerOverlay);
  // Uploaded SVG recolour: when an image upload is recoloured, inline its markup
  // with the new stroke/gradient (RecoloredHeroSvg) so the monochrome line art
  // takes `heroSvgStrokeColor` / gradient. An <img>/url() SVG is opaque and
  // cannot be recoloured directly.
  //
  // HERO-BG (#93) RECOLOUR FIX — the gate uses `isLikelySvgUrl`, which recognises
  // the SVG across EVERY real upload-URL shape. The uploaded hero is served via
  // the token-gated logo-media PROXY (`/api/storage/logo-file?path=…%2Fabc.svg&
  // token=…`), where `.svg` sits inside the URL-encoded `path` query param
  // followed by `&token=…`. The old inline `/\.svg(\?|#|$)/i` required `.svg` to
  // be followed by `?`/`#`/end, so it NEVER matched the proxy URL →
  // `recolorUploadedSvg` was false → the layer was never rendered → the raw grey
  // SVG showed through and the stroke/gradient controls did nothing (the bug).
  const isSvgUpload = isLikelySvgUrl(heroCover?.imageUrl);
  const svgUrl = heroCover?.imageUrl?.trim() || '';
  // HERO-BG (#93) Option A — "gradient = background only" for uploaded SVGs.
  // An uploaded SVG is ALWAYS inlined as the recoloured STROKE-ONLY overlay so
  // the line art renders on top of the cover background gradient. We decouple
  // this from `usesImage` (which is now FALSE for an SVG upload — its background
  // is a gradient, see resolveHeroCoverBackground) and from the stroke/gradient
  // presence (a default stroke still renders the line art when none is set —
  // RecoloredHeroSvg/recolorHeroSvgMarkup keep the uploaded colours unchanged in
  // that case). The fill stays TRANSPARENT (gradientStops={[]} below) so the
  // background gradient shows through the geometry.
  const recolorUploadedSvg = isSvgUpload && Boolean(svgUrl);

  // ONE-A4 CLIP GUARD (P1) — the full-page hero root is clamped to exactly one
  // A4 sheet with `overflow:hidden` (min==max==--report-page-height). It CARRIES
  // the FULL, never-truncated executive summary (the body Executive Summary
  // section is suppressed when the cover carries it), so if title + logos +
  // summary + disclaimer exceed one sheet the `overflow:hidden` SILENTLY CLIPS
  // the summary tail — that is data loss, not just layout drift.
  //
  // FIX — measure the hero root after layout; when its content overflows the
  // page box, progressively SHRINK the exec-summary auto-fit scale (down to a
  // legibility floor) so the whole summary fits. If it still overflows at the
  // floor, stamp `data-hero-overflow="clipped"` and emit a dev warning so the
  // clip is DETECTABLE rather than silent (and the summary is rendered with an
  // overflow-y:auto fallback so the viewer can scroll to the tail instead of
  // hard-cutting it). The compact banner is NOT page-clamped, so it is exempt.
  const heroRootRef = React.useRef<HTMLDivElement | null>(null);
  // 1 = no shrink; floors at 0.8 so text never drops below ~9px from the 11px base.
  const [execFitScale, setExecFitScale] = React.useState(1);
  const [execOverflowClipped, setExecOverflowClipped] = React.useState(false);

  React.useLayoutEffect(() => {
    // Only the page-clamped full-page hero can clip; the compact banner grows.
    if (isCompact || !heroExecutiveSummary) {
      setExecFitScale(1);
      setExecOverflowClipped(false);
      return;
    }
    const root = heroRootRef.current;
    if (!root) return;
    const FIT_FLOOR = 0.8;
    const FIT_STEP = 0.05;
    const overflows = () => root.scrollHeight > root.clientHeight + 1; // 1px epsilon
    if (!overflows()) {
      // Already fits at the current scale — but if we previously shrank, only
      // relax back up while it keeps fitting (cheap: reset to 1 and re-measure
      // happens on the next dependency change; here we just clear the clip flag).
      if (execOverflowClipped) setExecOverflowClipped(false);
      return;
    }
    // Content overflows: shrink toward the floor in steps until it fits.
    let scale = execFitScale;
    while (scale > FIT_FLOOR && overflows()) {
      scale = Math.max(FIT_FLOOR, Math.round((scale - FIT_STEP) * 100) / 100);
      root.style.setProperty('--hero-exec-fit-scale', String(scale));
    }
    const stillClipped = overflows();
    if (scale !== execFitScale) setExecFitScale(scale);
    if (stillClipped !== execOverflowClipped) setExecOverflowClipped(stillClipped);
    if (stillClipped && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        '[HeroImageCover] executive summary exceeds the one-A4 hero clamp even at the '
        + 'min font scale; the tail would be clipped. Rendering it scroll-to-fit and '
        + 'stamping data-hero-overflow="clipped" so the clip is detectable, not silent. '
        + `summary length=${heroExecutiveSummary.length}`,
      );
    }
    // Re-run when the inputs that affect content/box height change.
  }, [heroExecutiveSummary, isCompact, titleLabel, report.address, execFitScale, execOverflowClipped]);

  return (
    <>
      {/* FULL-BLEED IN PRINT — the on-screen Paged.js deck already flex-fills
          the cover wrapper chain to the bled sheet height
          (PaginatedReportView.buildPagedStylesheet). But the Chromium-native
          PDF export (server.ts / Cmd-P) renders this viewer with NO Paged.js,
          governed by index.css `@media print`: there the `#cover` content box
          is `var(--report-page-height) - margins` (≈253mm on A4) and NOTHING
          cascades a height onto the `cover-preview-frame → cover-preview-inner
          → hero-root` chain. The hero root's inline `min/max-height:
          var(--report-page-height)` (the full SHEET, ≈297mm) then disagrees with
          that smaller content box, so the navy panel either overflows or leaves
          a white band below the cover in the printed PDF.
          FIX — only in the export (`[data-print-export]`) and Cmd-P (`@media
          print`) paths, flex-fill the wrapper chain and let the hero root grow
          to fill `#cover` (`min-height:100%`), overriding the inline sheet-height
          cap so the gradient reaches the page bottom. The continuous scroll
          viewer is intentionally NOT matched (those selectors carry
          `[data-print-export]`/print media), so its `var(--report-page-height)`
          floor — which stops the hero collapsing to `min-h-[500px]` where the
          parent chain has no resolved height — is preserved. The deck path keeps
          its own `!important` pins, so all three print surfaces agree. */}
      <style>{`
        [data-print-export] #cover[data-cover-layout="hero-image"],
        [data-print-export] #cover[data-cover-layout="hero-image"] [data-cover-preview-frame],
        [data-print-export] #cover[data-cover-layout="hero-image"] [data-cover-preview-frame] > *,
        [data-print-export] #cover[data-cover-layout="hero-image"] [data-testid="cover-preview-inner"] {
          display: flex; flex-direction: column;
          flex: 1 1 auto; min-height: 100%;
        }
        [data-print-export] #cover[data-cover-layout="hero-image"] [data-cover-variant="hero-image"] {
          flex: 1 1 auto; min-height: 100%; max-height: none;
        }
        @media print {
          #cover[data-cover-layout="hero-image"],
          #cover[data-cover-layout="hero-image"] [data-cover-preview-frame],
          #cover[data-cover-layout="hero-image"] [data-cover-preview-frame] > *,
          #cover[data-cover-layout="hero-image"] [data-testid="cover-preview-inner"] {
            display: flex; flex-direction: column;
            flex: 1 1 auto; min-height: 100%;
          }
          #cover[data-cover-layout="hero-image"] [data-cover-variant="hero-image"] {
            flex: 1 1 auto; min-height: 100%; max-height: none;
          }
          #cover[data-cover-layout="hero-image"] [data-testid="cover-hero-image-platform-credit"] {
            top: calc(${REPORT_PAGE_MARGIN_MM.top}mm + 12px);
          }
        }
      `}</style>
    <div
      // Full-bleed within the cover frame (#cover uses p-0 for hero-image).
      // `h-full min-h-[500px]` fills the Studio A4 frame when the parent
      // chain sets an explicit height; min-height preserves legibility
      // in the production scroll viewer when no frame height exists.
      //
      // HERO LAYOUT (TC2): full-page keeps the page-filling behaviour;
      // compact drops to a fixed shorter banner (no `h-full`, lower
      // min-height) so the page-1 body sits higher up.
      //
      // ONE-PAGE HERO COVER — the full-page hero must always fill EXACTLY ONE
      // A4 page: the theme GRADIENT (and any SVG overlay) must full-bleed the
      // whole sheet, never collapse to a half-height card, and never spill onto
      // a second page.
      //
      // The paged deck pins `#cover[data-cover-layout="hero-image"]` (and the
      // whole frame→inner→hero wrapper chain) to `var(--report-page-height)`
      // with `!important` + `overflow:hidden`
      // (PaginatedReportView.buildPagedStylesheet); the Studio A4 preview pins
      // the frame chain to `1123px` (TemplatePreviewPane). But the CONTINUOUS
      // scroll viewer (SiteRagReportView) gives the cover→frame→inner chain NO
      // resolved height, so the hero root's Tailwind `h-full` (=`height:100%`)
      // resolves against an auto-height parent → `auto`, and the box collapses
      // toward its `min-h-[500px]` floor (~half of A4's 1123px). The v1.2.1
      // hero-cleanup added a `maxHeight` cap but no matching floor, so the
      // gradient painted only that ~half-height box (the regression).
      //
      // FIX — floor the full-page root at one page height (`min-height` =
      // `--report-page-height`, `100vh` fallback) IN ADDITION TO the equal
      // `maxHeight` cap. min == max == one A4 page, so the box is EXACTLY one
      // page in every context: it can neither shrink below a page (gradient
      // always full-bleeds) nor grow past it; with `overflow-hidden` tall
      // content self-clips, so no empty second sheet is ever produced. In the
      // deck + Studio the `!important`/explicit frame pins still win, so those
      // paths are unchanged. The image overlay (RecoloredHeroSvg, absolute
      // inset-0; only its inner `<svg>` is `object-contain`) sits ON TOP and
      // never affects this box height. `min-h-[500px]` (kept as a legibility
      // floor when the var is somehow unset) and `h-full` are preserved.
      // Hero min-height floors come from reportCoverTokens (the single source of
      // truth); the token string values equal the prior literals, so the
      // rendered className — and the HeroImageCover.layout.test geometry oracle
      // pinned to it — are byte-identical.
      className={`relative flex flex-col overflow-hidden ${
        isCompact ? COVER_HERO_COMPACT_MIN_H_CLASS : `h-full ${COVER_HERO_FULL_PAGE_MIN_H_CLASS}`
      }`}
      style={
        isCompact
          ? { background }
          : {
              background,
              minHeight: 'var(--report-page-height, 100vh)',
              maxHeight: 'var(--report-page-height, 100vh)',
            }
      }
      ref={heroRootRef}
      data-testid="cover-hero-image"
      data-cover-variant="hero-image"
      data-hero-background={isLayoutDiagramMode ? 'layout-diagram' : usesImage ? 'image' : 'gradient'}
      data-hero-text-tone={textTone}
      data-hero-layout={heroLayout}
      // ONE-A4 CLIP GUARD — `fit` once the auto-shrink kept the summary inside
      // one sheet; `clipped` (only if it still overflows at the min scale) makes
      // the otherwise-silent clip DETECTABLE for QA / the visual matrix.
      data-hero-overflow={isCompact ? undefined : execOverflowClipped ? 'clipped' : 'fit'}
    >
      {/* HERO-BG (#93) — recoloured uploaded SVG. RecoloredHeroSvg fetches the
          SVG markup (same-origin proxy), recolours its stroke/fill directly and
          inlines it (no CORS-dependent CSS mask, no opaque-box artefact for
          transparent line art); it falls back to a same-origin CSS mask while
          loading / on fetch failure. */}
      {recolorUploadedSvg && (
        // STROKE ONLY — `gradientStops={[]}` keeps the SVG fill transparent so
        // the cover BACKGROUND gradient (resolveHeroCoverBackground) shows
        // through the geometry. The gradient is NEVER painted INTO the SVG
        // (Option A: "gradient = background only").
        <RecoloredHeroSvg
          imageUrl={svgUrl}
          strokeColor={heroSvgStrokeColor}
          gradientStops={[]}
          // HERO IMAGE ALIGN — when the author has positioned the uploaded SVG
          // (9-position grid), RecoloredHeroSvg renders it `object-contain` +
          // the mapped object-position (e.g. a site-grid SVG sits bottom-left).
          // Absent/`center` ⇒ the byte-stable full-bleed `object-cover` default.
          align={heroCover?.imageAlign}
        />
      )}

      {/* HERO-BG (#93) — generated site-layout diagram as the full-bleed
          background (layout-diagram mode) or composited over the chosen
          background (heroIncludeLayoutDiagram). Pure client-side geometry. */}
      {(isLayoutDiagramMode || compositeLayoutDiagram) && (
        <div
          className="absolute inset-0 pointer-events-none flex items-center justify-center p-[40px]"
          data-testid="cover-hero-fullbleed-diagram"
          data-hero-diagram-mode={isLayoutDiagramMode ? 'background' : 'composite'}
        >
          <HeroCoverBoundaryOverlay
            report={report}
            layers={effectiveBranding.kmzLayers}
            // HERO-COVER CLEAN-MAP — render the boundary + layer GEOMETRY only,
            // with NO text annotations (leader names / area chips / utility
            // legend). The crowded labels ("Flood — part 2 of 4", "Entergy
            // 230kV", "5mi. Fiber Ext.", …) just clutter the cover diagram.
            hideAllLabels
            textTone={textTone}
            strokeColor={heroSvgStrokeColor}
            fillGradient={heroSvgGradient.length >= 2 ? heroSvgGradient : undefined}
            className="h-full w-full max-w-none aspect-auto"
          />
        </div>
      )}

      {/* Scrim — dark on photos / dark gradients; light wash on pale gradients. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: resolveHeroCoverScrim(usesImage, textTone) }}
      />

      <div
        // RRQ DEPLOY-BLOCKER FIX (D-1) — kill the hero dead-space. The full-page
        // hero pins its root to EXACTLY one A4 sheet; with `items-center` (cross
        // axis = vertical) a short title cluster (eyebrow → logo → address → h1 →
        // prepared-for → optional exec summary) math-CENTERED in the sheet,
        // leaving ~40% empty above and ~35% below (worst on thin / no-exec-summary
        // reports). Anchor the cluster to the UPPER-THIRD board-deck position
        // instead: `items-start` + a deliberate top inset (`pt-[18%]`) for the
        // full-page layout. Compact keeps `items-center` (it is a short banner
        // with the page-1 body directly beneath, so centering reads correctly).
        // Pure layout — no content change.
        className={`relative z-10 flex flex-1 px-[60px] ${
          isCompact ? 'items-center py-6' : 'items-start pt-[18%] pb-6'
        } ${onDark ? 'text-white' : 'text-slate-900'}`}
        // On a pale gradient the text tone follows the resolved theme ink via
        // an inline color (which WINS over the retained `text-slate-900`
        // utility — kept so the text-tone contract/tests still hold). On a dark
        // photo/gradient we keep `text-white` for legibility on the scrim.
        style={onDark ? undefined : { color: roleColor(theme, 'ink') }}
      >
        <div className="w-full flex items-stretch flex-wrap gap-[0.4rem]">
          <div className="flex-1 min-w-0 flex flex-col gap-4">
            <div className="space-y-4">
              <div
                className={`text-xs font-bold uppercase tracking-[0.4em] ${onDark ? 'opacity-80' : 'opacity-70'}`}
                data-testid="cover-site-name-header"
              >
                {resolveEyebrowWordmark(effectiveBranding.siteWordmark)}
              </div>
              {(effectiveBranding.primaryLogoUrl || effectiveBranding.secondaryLogoUrl) && (
                <div className="flex items-center gap-2" data-testid="cover-hero-image-logos" data-audience-mode={effectiveBranding.audienceMode}>
                  {effectiveBranding.primaryLogoUrl && (
                    // Primary brand mark. The platform-default DEEP·ATOMIC
                    // wordmark renders as the theme-aware WHITE inline logo on
                    // a transparent dark surface (no white chip). An uploaded
                    // customer logo keeps its true colour on an always-light
                    // backing chip when the surface is dark, or renders plainly
                    // on a pale gradient.
                    <CoverPrimaryLogo
                      src={effectiveBranding.primaryLogoUrl}
                      alt={effectiveBranding.primaryLogoAlt}
                      tone={textTone}
                      heightPx={24}
                      imgClassName="h-6 max-w-[104px] object-contain"
                      data-testid="cover-hero-image-logo-primary"
                    />
                  )}
                  {effectiveBranding.audienceMode === 'customer' && effectiveBranding.secondaryLogoUrl && (
                    <>
                      <span
                        className={`mx-2 h-5 w-px ${onDark ? 'bg-white/40' : ''}`}
                        // On-light divider follows the theme ink at low alpha
                        // instead of the fixed `bg-text-primary/25` token.
                        style={onDark ? undefined : { backgroundColor: roleColorAlpha(theme, 'ink', 0.25) }}
                        aria-hidden="true"
                      />
                      <CoverCustomerLogo
                        src={effectiveBranding.secondaryLogoUrl}
                        alt={effectiveBranding.secondaryLogoAlt}
                        tone={textTone}
                        className="h-6 max-w-[104px] object-contain"
                        data-testid="cover-hero-image-logo-secondary"
                      />
                    </>
                  )}
                </div>
              )}
              <div className={`text-sm font-medium ${onDark ? 'opacity-90' : 'opacity-80'}`}>{report.address}</div>
              <h1
                className="font-bold leading-tight tracking-tight"
                // Title size keys off the resolved headingScale (editorial >
                // technical > compact) instead of a fixed `text-5xl`, scaled up
                // for the cover display. Display font falls back to headingFont.
                // On a pale gradient the title ink follows the theme; on-dark it
                // inherits the container's `text-white`.
                style={{
                  fontFamily: theme.displayFont || theme.headingFont,
                  // v1.3.4 PRINT-DENSITY (#23): drop the display multiplier
                  // 2 -> 1.6 so the title reads as a strong board-deck headline
                  // while reclaiming vertical space for the one-A4 clamp.
                  fontSize: `calc(${reportTypographySizes(theme).h2} * 1.6)`,
                  ...(onDark ? {} : { color: roleColor(theme, 'ink') }),
                }}
              >
                {titleLabel}
              </h1>
              <div className={onDark ? 'opacity-90' : 'opacity-85'}>
                <PreparedForLine
                  report={report}
                  isAdmin={isAdmin}
                  pickerOpen={pickerOpen}
                  onTogglePicker={onTogglePicker}
                  onSelectArchetype={onSelectArchetype}
                />
              </div>
              {/* v1.3.4 PRINT-DENSITY (#23): the KPI chip strip was REMOVED from
                   the full-page hero cover. The hero cover must clamp to exactly
                   one A4 sheet, and the headline KPIs already render in the
                   Executive Letter / page-1 body immediately after the cover —
                   carrying them on the cover too added height that pushed the
                   cover toward a second sheet with no informational gain. The
                   non-full-page covers (classic / minimal / data-dense) keep
                   their chip strips: they flow inline with the body, not as a
                   clamped full-bleed page, and data-dense is chip-centric by
                   design. (HERO PRIMARY-BTM REMOVAL already filtered the `power`
                   run-in; the whole strip is now gone.) */}
            </div>
            {/* EXEC-SUMMARY ON COVER (decision 4) — replaces the former
                 mid-cover disclaimer block. The hero now CARRIES the
                 executive summary; the body section + TOC entry are
                 suppressed upstream (coverCarriesExecSummary) to avoid
                 duplication. Collapses cleanly when absent. */}
            {heroExecutiveSummary && (
              <div
                // v1.3.4 PRINT-DENSITY (#23): tightened to 11px / snug leading /
                // pt-3 so the FULL executive summary (never truncated — the body
                // section is suppressed when the cover carries it, so the prose
                // must stay whole) packs more compactly within the one-A4 clamp.
                //
                // ONE-A4 CLIP GUARD (P1): the font-size is `11px * --hero-exec-
                // fit-scale` (default 1; auto-shrunk toward 0.8 = ~9px when the
                // cover would otherwise overflow). When even the floor overflows,
                // `execOverflowClipped` switches the block to overflow-y:auto so
                // the viewer can reach the tail instead of a silent hard-cut.
                className={`leading-snug pt-3 border-t max-w-2xl ${
                  execOverflowClipped ? 'overflow-y-auto' : ''
                } ${onDark ? 'opacity-90 border-white/30' : 'opacity-90'}`}
                // On-light divider follows the theme ink at low alpha instead
                // of a fixed token. fontSize keys off the auto-fit scale var.
                style={{
                  fontSize: `calc(11px * var(--hero-exec-fit-scale, ${execFitScale}))`,
                  ...(onDark ? {} : { borderTopColor: roleColorAlpha(theme, 'ink', 0.2) }),
                }}
                data-testid="cover-hero-image-executive-summary"
                data-hero-exec-fit-scale={execFitScale}
              >
                <div
                  className={`text-[10px] font-bold uppercase tracking-widest mb-1.5 ${onDark ? 'opacity-75' : 'opacity-65'}`}
                >
                  Executive summary
                </div>
                <p className="whitespace-pre-line">{heroExecutiveSummary}</p>
              </div>
            )}
          </div>
          {/* When the diagram is painted full-bleed (background or composite),
              suppress the duplicate side-panel diagram. */}
          {((!isLayoutDiagramMode && !compositeLayoutDiagram && (hasBoundaryOverlay || hasKmzLayerOverlay)) || sideImageUrl) && (
            <div
              // HERO-COVER MAP SIZE — enlarged from w-[34%]/max-w-[360px]/
              // max-h-[320px] so the clean (label-free) boundary map reads
              // bigger on the cover. Still width-capped + `self-center` so it
              // stays within the cover layout and never crowds the title column.
              className="w-[42%] min-w-[240px] max-w-[460px] max-h-[420px] self-center rounded-xl overflow-hidden"
              data-testid="cover-hero-image-side-image"
            >
              {(!isLayoutDiagramMode && !compositeLayoutDiagram && (hasBoundaryOverlay || hasKmzLayerOverlay)) ? (
                <div className="h-full w-full pointer-events-none flex flex-col">
                  <HeroCoverBoundaryOverlay
                    report={report}
                    layers={effectiveBranding.kmzLayers}
                    // HERO-COVER CLEAN-MAP — geometry only, no text annotations.
                    hideAllLabels
                    textTone={textTone}
                    className="h-full w-full max-w-none aspect-auto"
                  />
                </div>
              ) : (
                <img
                  src={sideImageUrl}
                  alt={`${effectiveBranding.siteWordmark} hero`}
                  className="h-full w-full object-cover"
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* CONFIDENTIAL DISCLAIMER — bottom-right footnote (decision 4), out of
          the main column now occupied by the executive summary.
          OVERLAP FIX — formerly `absolute bottom-0 right-0`, which let the
          flowing executive-summary paragraph COLLIDE with the disclaimer in the
          fixed-height print page (the summary grows downward into the absolutely
          positioned footnote). It now participates in the flex-column FLOW as
          the last child (`mt-auto` pins it to the bottom of the page box, and
          `relative` keeps it on its own line BELOW the `flex-1` content block),
          so the two can never overlap in either the fluid viewer or the fixed
          ~700px print/paged width — the content block simply yields the
          disclaimer its natural height. `flex-shrink-0` guarantees the footnote
          keeps its full height even when the summary is long. Sits above the
          scrim (z-10), right-aligned and width-capped (self-end) so it still
          reads as a footnote. Sized 8px/80% to match the other six covers'
          disclaimer footnote (cover-content batch). On a dark photo/scrim it
          stays `text-white`; on a pale gradient it uses the clamped report ink
          (readable dark-on-white, per the light-doc lock). */}
      <div
        // v1.3.4 PRINT-DENSITY (#23): snug leading + pb-4 so the terse legal
        // block sits compactly at the cover foot within the one-A4 clamp.
        // Substance (beta + independent-validation) preserved in the archetype
        // disclaimer text (archetypeTemplates.ts).
        className={`relative z-10 mt-auto flex-shrink-0 self-end px-[60px] pb-4 text-right text-[8px] leading-snug max-w-md opacity-80 ${onDark ? 'text-white' : ''}`}
        style={onDark ? undefined : { color: roleColor(theme, 'ink') }}
        data-testid="cover-hero-image-disclaimer"
      >
        {disclaimer}
      </div>

      {/* GATEWAY POWER platform credit — TOP-right corner, 12px down, on the
          60px gutter, inside the cover's empty top inset (full-page pt-[18%], compact
          py-6). NOT the foot line: that is shared with the one-sheet-clamped,
          auto-fitted executive summary, which at print width reaches the clamp's
          bottom edge — a bottom-anchored credit printed over its last line.
          Absolute, so the auto-fit budget is unchanged. In PRINT the export
          root pulls this cover 14mm up into the sheet's top margin for full
          bleed (index.css, REPORT_COVER_BLEED_MARGIN_CSS) and nothing in that
          margin band prints, so the print offset adds the same 14mm
          (REPORT_PAGE_MARGIN_MM.top; pinned by ReportPlatformCredit.test).
          The artwork follows the surface: dark artwork on a photo/dark scrim,
          light artwork on a pale gradient; the text matches the disclaimer's
          80 % ink. */}
      <ReportPlatformCredit
        tone={onDark ? 'on-dark' : 'on-light'}
        color={onDark ? undefined : roleColorAlpha(theme, 'ink', 0.8)}
        className={`absolute top-3 right-[60px] z-10${onDark ? ' text-white/80' : ''}`}
        testId="cover-hero-image-platform-credit"
      />
    </div>
    </>
  );
};
